import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { radius, space, textStyle } from "@passfirst/ui";

import { type Colors, useColors } from "./theme";
import { useNetworkStatus } from "./use-network-status";

// Thin status bar pinned to the top of the app. Renders nothing while
// online and untouched; flips to a red "You're offline" banner during
// outages and a brief green "Reconnected" pulse when connectivity
// comes back. Sits above the navigator (mounted in _layout.tsx) so it
// shows on every screen without per-screen plumbing.
export function NetworkBanner(): JSX.Element | null {
  const { isOnline, justReconnected } = useNetworkStatus();
  const colors = useColors();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  if (isOnline && !justReconnected) return null;

  const offline = !isOnline;
  return (
    <View
      pointerEvents="none"
      style={[
        styles.banner,
        offline ? styles.bannerOffline : styles.bannerReconnected,
        { paddingTop: space[2] + insets.top },
      ]}
      accessibilityLiveRegion="polite"
      accessibilityRole="alert"
    >
      <Text style={styles.text}>
        {offline ? "You're offline" : "Reconnected"}
      </Text>
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    banner: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      paddingVertical: space[2],
      paddingHorizontal: space[4],
      zIndex: 1000,
      alignItems: "center",
      borderBottomLeftRadius: radius.sm,
      borderBottomRightRadius: radius.sm,
    },
    bannerOffline: { backgroundColor: colors.error },
    bannerReconnected: { backgroundColor: colors.success },
    text: { ...textStyle.bodySmall, color: "#fff", fontWeight: "600" },
  });
}
